import { Plus } from '@phosphor-icons/react';
import { cloneElement, ReactElement, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'core/components';
import { useCanManageParts } from 'corporate/hooks';
import { ICorporatePart, ICorporatePartCostListItem } from 'corporate/types';
import { Color } from 'theme/enums';

import { UpsertCorporatePartCost } from './UpsertCorporatePartCost';

interface IProps {
  corporatePart: Partial<ICorporatePart>;
  cost?: ICorporatePartCostListItem;
  buttonElement?: ReactElement;
  onSubmitSuccess?: () => void;
  onRemoveSuccess?: () => void;
  disabled?: boolean;
}

export const UpsertCorporatePartCostButton = ({
  corporatePart,
  cost,
  buttonElement,
  onSubmitSuccess,
  onRemoveSuccess,
  disabled = false,
}: IProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { t } = useTranslation();
  const canManageParts = useCanManageParts();

  const button = useMemo(() => {
    if (buttonElement) {
      return cloneElement(buttonElement, {
        onClick: disabled ? undefined : () => setIsOpen(true),
      });
    }

    return (
      <Button
        text={t('buttonLabelAddPartCost')}
        color={Color.SUCCESS}
        startDecorator={<Plus />}
        onClick={() => setIsOpen(true)}
        disabled={disabled}
      />
    );
  }, [buttonElement, disabled, t]);

  if (!canManageParts) {
    return null;
  }

  return (
    <>
      {button}

      {isOpen && (
        <UpsertCorporatePartCost
          corporatePart={corporatePart}
          cost={cost}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          onSubmitSuccess={onSubmitSuccess}
          onRemoveSuccess={onRemoveSuccess}
        />
      )}
    </>
  );
};
